/**
 * BridgeFetchStepper — compact vertical progress for a bridge re-fetch
 * (BLU-880): Sent to bridge → Picked up → Downloaded → RIPped → Uploaded → Done.
 *
 * Production-app language (PRODUCTION_DESIGN.md §4.1): colour encodes STATE —
 * emerald = done, amber = in progress / gone quiet, red = failed, muted = not
 * yet. Every coloured state pairs with an icon AND text (never colour alone):
 * the in-progress pulse has a static reduced-motion twin (§5.3) and the
 * gone-quiet row swaps to a Clock so the two differ without motion. The
 * current row carries the server stamp as a relative "· 12s ago"
 * (`tabular-nums`, ticks once a second while the run is live). It is hidden
 * while TIMED OUT: the queue re-serves an open issue on every poll, which
 * refreshes the stamp without advancing the run, so "· 2s ago" right above
 * "no response for a while" would contradict the timeout it sits under.
 *
 * A11y (§13): the list is labelled by a visible heading; each row carries an
 * sr-only state; ONE polite live region names the current step (and carries
 * the gone-quiet copy); the failure line is a `role="alert"` on its own.
 *
 * TIMEOUT is a soft "taking longer than usual", not a failure — amber, the
 * stream is still listening, and the parent re-enables Try again. FAILED is
 * terminal — red.
 */
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Check, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  BRIDGE_FETCH_STEPS,
  formatRelativeAgo,
  isTerminalBridgeFetchState,
  knownBridgeFailReason,
  type BridgeFetchSnapshot,
  type BridgeFetchStep,
} from '../bridgeFetchState';

interface BridgeFetchStepperProps {
  snapshot: BridgeFetchSnapshot;
  /** The row a `failed` snapshot paints red (from `useBridgeFetchProgress`). */
  failedStep: BridgeFetchStep | null;
  /** No advance for the state's stale window (150 s before pickup, 5 minutes
   *  after): the active row turns into a "gone quiet" warning. */
  timedOut: boolean;
}

type RowTone = 'done' | 'active' | 'failed' | 'stale' | 'pending';

const AGO_TICK_MS = 1_000;

export function BridgeFetchStepper({ snapshot, failedStep, timedOut }: BridgeFetchStepperProps) {
  const { t } = useTranslation('production');
  const headingId = useId();

  const isFailed = snapshot.state === 'failed';
  const isResolved = snapshot.state === 'resolved';
  const terminal = isTerminalBridgeFetchState(snapshot.state);
  const activeStep: BridgeFetchStep =
    snapshot.state === 'failed' ? (failedStep ?? 'served') : snapshot.state;
  const activeIdx = BRIDGE_FETCH_STEPS.indexOf(activeStep);

  // "· 12s ago" on the current row — ticks while the run is live, frozen once
  // terminal (a resolved/failed stamp is a fact, not a stopwatch).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (terminal) return;
    const id = setInterval(() => setNow(Date.now()), AGO_TICK_MS);
    return () => clearInterval(id);
  }, [terminal]);
  const ago = formatRelativeAgo(snapshot.at, now);
  const agoText = ago ? t(`issues.printFiles.bridgeFetch.ago.${ago.unit}`, { count: ago.count }) : null;

  const toneFor = (idx: number): RowTone => {
    if (isResolved) return 'done';
    if (idx < activeIdx) return 'done';
    if (idx > activeIdx) return 'pending';
    if (isFailed) return 'failed';
    return timedOut ? 'stale' : 'active';
  };

  const reasonCode = knownBridgeFailReason(snapshot.reason);
  const failureText = isFailed
    ? reasonCode
      ? t('issues.printFiles.bridgeFetch.failedWithReason', {
          reason: t(`issues.printFiles.bridgeFetch.reason.${reasonCode}`),
        })
      : snapshot.reason
        ? t('issues.printFiles.bridgeFetch.failedWithReason', { reason: snapshot.reason })
        : t('issues.printFiles.bridgeFetch.failed')
    : null;
  // F1b: still `queued` = the bridge never picked it up (it may be off);
  // after pickup = it went quiet mid-job (large files take a while).
  const staleText = !isFailed && timedOut
    ? t(snapshot.state === 'queued'
      ? 'issues.printFiles.bridgeFetch.timeoutNotPickedUp'
      : 'issues.printFiles.bridgeFetch.timeout')
    : null;
  const currentStepText = t('issues.printFiles.bridgeFetch.currentStep', {
    step: t(`issues.printFiles.bridgeFetch.step.${activeStep}`),
  });

  return (
    <div className="space-y-2" data-testid="bridge-fetch-stepper">
      <p id={headingId} className="text-xs font-medium text-muted-foreground">
        {t('issues.printFiles.bridgeFetch.title')}
      </p>
      <ol className="space-y-0" aria-labelledby={headingId} data-state={snapshot.state}>
        {BRIDGE_FETCH_STEPS.map((step, idx) => {
          const tone = toneFor(idx);
          const isLast = idx === BRIDGE_FETCH_STEPS.length - 1;
          // The stamp belongs to the row the run is ON: the current row, or
          // "Done" once resolved. Suppressed once timed out — a re-serve
          // refreshes the stamp without advancing, and "· 2s ago" would
          // contradict the gone-quiet line below.
          const showAgo =
            agoText !== null && !timedOut && (isResolved ? isLast : idx === activeIdx);
          return (
            <li
              key={step}
              className="relative flex min-h-8 items-start gap-2.5"
              data-step={step}
              data-tone={tone}
              aria-current={tone === 'active' || tone === 'stale' || tone === 'failed' ? 'step' : undefined}
            >
              {/* Connector — painted emerald once the step above is done. */}
              {!isLast && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute left-[9px] top-5 h-[calc(100%-0.75rem)] w-0.5 rounded-full transition-colors duration-150 motion-reduce:transition-none',
                    tone === 'done' ? 'bg-emerald-600' : 'bg-border',
                  )}
                />
              )}
              <span
                aria-hidden="true"
                data-marker={tone}
                className={cn(
                  'relative z-10 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-150 motion-reduce:transition-none',
                  tone === 'done' && 'border-emerald-600 bg-emerald-600 text-white',
                  tone === 'active' && 'border-amber-500 bg-amber-500/15',
                  tone === 'stale' && 'border-amber-500 bg-amber-500/15 text-amber-700 dark:text-amber-400',
                  tone === 'failed' && 'border-red-500 bg-red-500 text-white',
                  tone === 'pending' && 'border-muted-foreground/30 bg-background',
                )}
              >
                {tone === 'done' && <Check className="h-3 w-3" strokeWidth={3} />}
                {tone === 'failed' && <AlertCircle className="h-3 w-3" strokeWidth={3} />}
                {tone === 'stale' && <Clock className="h-3 w-3" strokeWidth={3} />}
                {tone === 'active' && (
                  <span className="h-2 w-2 rounded-full bg-amber-500 motion-safe:animate-pulse" />
                )}
              </span>
              <span
                className={cn(
                  'flex min-w-0 flex-wrap items-baseline gap-x-1.5 pb-3 text-sm leading-5',
                  tone === 'done' && 'text-foreground',
                  (tone === 'active' || tone === 'stale') && 'font-medium text-foreground',
                  tone === 'failed' && 'font-medium text-red-600 dark:text-red-400',
                  tone === 'pending' && 'text-muted-foreground',
                )}
              >
                {t(`issues.printFiles.bridgeFetch.step.${step}`)}
                <span className="sr-only">{t(`issues.printFiles.bridgeFetch.rowState.${tone}`)}</span>
                {showAgo && (
                  <span
                    className="text-xs font-normal tabular-nums text-muted-foreground"
                    data-testid="bridge-fetch-ago"
                  >
                    {`· ${agoText}`}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
      {/* ONE polite live region: names the current step for screen readers and
          carries the visible gone-quiet copy. */}
      <p
        role="status"
        aria-live="polite"
        className={cn('text-sm text-amber-700 dark:text-amber-400', !staleText && 'sr-only')}
      >
        <span className="sr-only">{currentStepText} </span>
        {staleText}
      </p>
      {failureText && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {failureText}
        </p>
      )}
    </div>
  );
}
